/**
 * Pick and mix promotions from state.pickAndMix.all (GET /all) into the
 * holocron order-calc shape (spec §4).
 *
 * This is a port of amen app/services/order-calc/context-builder/
 * normalise-pick-and-mixes.js, plus the active filter of amen
 * PickAndMix.findActivePrivate. The /all response is filtered when the page
 * loads, so a tab that stays open can hold a promotion that is no longer
 * active. We filter again at `now`.
 */

const toTime = value => new Date(value).getTime();

/**
 * The same window as amen pick-and-mix-model.js activeFilter(): started, and
 * not ended or with no end (a permanent promotion, B14).
 * @param   {Object}  pickAndMix Promotion from /all.
 * @param   {number}  now        Epoch milliseconds.
 * @returns {boolean}
 */
export const isActivePickAndMix = (pickAndMix, now) => {
  if (!pickAndMix.valid_from || toTime(pickAndMix.valid_from) > now) return false;

  return pickAndMix.valid_until == null || toTime(pickAndMix.valid_until) >= now;
};

/**
 * @param   {Object} pickAndMix   Promotion from /all. products[] is populated.
 * @param   {string} currencyCode ISO 4217 code of the cart.
 * @returns {Object}              { id, name, slug, productIds, tiers: [{ quantity, price }] }
 * @throws  {Error}               When a tier has no price for currencyCode.
 */
export default function normalisePickAndMix(pickAndMix, currencyCode) {
  return {
    id: String(pickAndMix._id),
    name: pickAndMix.name,
    slug: pickAndMix.slug,
    productIds: (pickAndMix.products ?? []).map(product => String(product?._id ?? product)),
    tiers: (pickAndMix.tiers ?? []).map((tier, i) => {
      const price = tier.price?.[currencyCode];

      if (typeof price !== 'number') {
        throw new Error(`Price missing for tier ${i} - ${currencyCode}`);
      }

      return {
        quantity: tier.quantity,
        // Some tier prices are stored as a float (catalogue D25)
        price: Math.round(price),
      };
    }),
  };
}
